// AdminPartnerOrders — vista de administración de TODOS los pedidos a
// socios. Soporta dos rutas:
//   /admin/partners/orders           → lista global
//   /admin/partners/:partnerId/orders → solo de un negocio
//
// Filtros: estado, tipo (STAY|PURCHASE), búsqueda libre (cliente, ref,
// teléfono, dirección). Tap a una fila → /myorders/:orderId (PartnerOrderDetail
// ya soporta vista admin).

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from 'common';
import {
  ORDER_STATUS, statusColor, accentColorForType, isStayOrder,
} from 'common/src/other/PartnerUtils';
import {
  Box, Stack, Paper, Typography, TextField, MenuItem, Chip, Avatar,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import moment from 'moment/min/moment-with-locales';
import CircularLoading from '../components/CircularLoading';
import { FONT_FAMILY } from '../common/sharedFunctions';

const STATUS_LABEL = {
  PENDING: 'Pendiente',
  CONFIRMED: 'Confirmado',
  IN_PREPARATION: 'En preparación',
  READY: 'Listo',
  OUT_FOR_DELIVERY: 'En camino',
  COMPLETED: 'Completado',
  CANCELLED: 'Cancelado',
  REJECTED: 'Rechazado',
  CHECKED_IN: 'Check-in',
  CHECKED_OUT: 'Check-out',
  NO_SHOW: 'No-show',
};

export default function AdminPartnerOrders() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { partnerId } = useParams();
  const partnerorderdata = useSelector(s => s.partnerorderdata);
  const partnerdata = useSelector(s => s.partnerdata);
  const { fetchAllPartnerOrders, fetchPartnerOrdersByPartner, fetchPartners } = api;

  const [statusFilter, setStatusFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (partnerId) {
      dispatch(fetchPartnerOrdersByPartner(partnerId));
    } else {
      dispatch(fetchAllPartnerOrders());
    }
    dispatch(fetchPartners());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  const partnerName = useMemo(() => {
    if (!partnerId) return null;
    return (partnerdata.partners || []).find(p => p.id === partnerId)?.businessName || partnerId;
  }, [partnerId, partnerdata.partners]);

  const rows = useMemo(() => {
    const list = partnerorderdata?.orders || [];
    const q = search.toLowerCase();
    return list
      .filter(o => statusFilter === 'ALL' || o.status === statusFilter)
      .filter(o => typeFilter === 'ALL' || o.orderType === typeFilter)
      .filter(o => {
        if (!q) return true;
        return (
          (o.reference || '').toLowerCase().includes(q) ||
          (o.customerName || '').toLowerCase().includes(q) ||
          (o.customerPhone || '').toLowerCase().includes(q) ||
          (o.partnerName || '').toLowerCase().includes(q) ||
          (o.drop?.add || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }, [partnerorderdata?.orders, statusFilter, typeFilter, search]);

  const counts = useMemo(() => {
    const list = partnerorderdata?.orders || [];
    const c = { ALL: list.length };
    list.forEach(o => { c[o.status] = (c[o.status] || 0) + 1; });
    return c;
  }, [partnerorderdata?.orders]);

  if (!partnerorderdata?.orders && partnerorderdata?.loading) return <CircularLoading />;

  return (
    <Box sx={{ p: 2, fontFamily: FONT_FAMILY }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate(-1)}><ArrowBackIcon /></IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {partnerId ? `Pedidos · ${partnerName}` : 'Pedidos · todos los socios'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {rows.length} de {counts.ALL || 0} pedidos
          </Typography>
        </Box>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          select size="small" label="Estado"
          value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="ALL">Todos ({counts.ALL || 0})</MenuItem>
          {Object.values(ORDER_STATUS).map(s => (
            <MenuItem key={s} value={s}>{STATUS_LABEL[s] || s} ({counts[s] || 0})</MenuItem>
          ))}
        </TextField>
        <TextField
          select size="small" label="Tipo"
          value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="ALL">Todos</MenuItem>
          <MenuItem value="STAY">Estadía (hotel)</MenuItem>
          <MenuItem value="PURCHASE">Compra</MenuItem>
        </TextField>
        <TextField
          size="small" placeholder="Buscar cliente, ref, dirección…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          fullWidth
        />
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Ref · Cliente</TableCell>
              {!partnerId && <TableCell>Negocio</TableCell>}
              <TableCell>Tipo</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell>Conductor</TableCell>
              <TableCell>Creado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(o => (
              <TableRow key={o.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/myorders/${o.id}`)}>
                <TableCell>
                  <Typography sx={{ fontWeight: 700 }}>#{(o.reference || o.id || '').slice(0, 8).toUpperCase()}</Typography>
                  <Typography variant="caption" color="text.secondary">{o.customerName || 'Cliente'}</Typography>
                </TableCell>
                {!partnerId && (
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar src={o.partnerCover || undefined} variant="rounded" sx={{ width: 32, height: 32 }}>
                        {(o.partnerName || '?').slice(0,1)}
                      </Avatar>
                      <Typography variant="body2" noWrap>{o.partnerName || '—'}</Typography>
                    </Stack>
                  </TableCell>
                )}
                <TableCell>
                  <Chip
                    size="small"
                    label={isStayOrder(o) ? 'Estadía' : 'Compra'}
                    sx={{
                      backgroundColor: `${accentColorForType(o.partnerType)}1a`,
                      color: accentColorForType(o.partnerType),
                      fontWeight: 600,
                    }}
                  />
                  {o.selfService ? (
                    <Chip size="small" label="auto" sx={{ ml: 0.5, fontSize: 10, height: 18 }}/>
                  ) : null}
                </TableCell>
                <TableCell align="right">
                  <Typography sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                    {(o.currency || '')} ${Number(o.total || 0).toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    label={STATUS_LABEL[o.status] || o.status}
                    sx={{
                      backgroundColor: `${statusColor(o.status)}22`,
                      color: statusColor(o.status),
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
                <TableCell>
                  {o.driverName || (o.driver ? o.driver.slice(0, 6) : <Typography variant="caption" color="text.secondary">—</Typography>)}
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {o.createdAt ? moment(o.createdAt).fromNow() : '—'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={partnerId ? 6 : 7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    Sin pedidos para los filtros actuales.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
